import { useCallback, useState } from 'react';
import { cn } from '@divlab/divanui';

import * as Profile from '@Queries/Profile';
import useNavigation from '@Navigation/useNavigation';
import { useDeps } from '@Contexts/DI';
import AuthorizationForm from './elems/AuthorizationForm';
import ClarificationProfileForm from './elems/ClarificationProfileForm';
import styles from './Form.module.css';
import './styles.raw.css';

import type { ChangeEvent, FC, HTMLAttributes } from 'react';
import type { ProfileData } from '@Types/Profile';

export type FormData = {
  phone: string;
  email: string;
  firstName: string;
};

type Step = 'authorization' | 'clarification';

interface FormProps extends HTMLAttributes<HTMLDivElement> {
  onCodeSent?(): void;
  onAuthorization?(): void;
  onClarification?: () => void;
}

const Form: FC<FormProps> = (props) => {
  const { className, onCodeSent, onAuthorization, onClarification, ...restProps } = props;
  const [step, setStep] = useState<Step>('authorization');
  const { analytics } = useDeps();
  const navigation = useNavigation();
  const settings = Profile.useSettings();
  const { data } = Profile.useProfile();
  const [formData, setFormData] = useState<FormData>({
    phone: data?.phone || '',
    email: '',
    firstName: '',
  });

  const handleFormDataChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleResetPhone = () => setFormData((prev) => ({ ...prev, phone: '' }));

  const handleDefaultSuccess = useCallback(async () => {
    if (onAuthorization) onAuthorization();

    Profile.updateWelcomePopup({ hovered: true });

    //после того как показали попап его нужно скрыть через 5 секунд
    setTimeout(() => Profile.updateWelcomePopup({ hovered: false }), 5000);

    if (settings.redirectAfterAuth) {
      await navigation.openPage({ url: settings.redirectAfterAuth });
      Profile.updateSettings({ redirectAfterAuth: null });
      return;
    }
    await navigation.openPage({ url: '/cabinet' });
  }, [navigation, settings, onAuthorization]);

  const handleSuccessAuth = useCallback(
    (profile: ProfileData, needClarification: boolean) => {
      analytics.dispatchEvent('profile.signIn', { action: 'success', profile });

      if (needClarification) {
        setStep('clarification');
        onClarification();
      } else handleDefaultSuccess();
    },
    [analytics, handleDefaultSuccess, onClarification],
  );

  return (
    <div className={cn(styles.form, className)} {...restProps}>
      {step === 'authorization' && (
        <AuthorizationForm
          defaultData={formData}
          onChangeData={handleFormDataChange}
          onSuccess={handleSuccessAuth}
          onResetPhone={handleResetPhone}
          data-testid='authorization'
          onCodeSent={onCodeSent}
        />
      )}
      {step === 'clarification' && (
        <ClarificationProfileForm
          defaultData={formData}
          onSuccess={handleDefaultSuccess}
          onChangeData={handleFormDataChange}
          data-testid='clarification'
        />
      )}
    </div>
  );
};

export default Form;
